import { assertNever, bigNumberify, NormalizedNumber } from '@sparkdotfi/common-universal'
import { erc4626Abi, formatUnits, parseUnits, zeroAddress } from 'viem'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'

import { PriceConfig, PriceStrategy } from './types'
import { chronicleOracleAbi } from '@/config/abis/chronicleOracleAbi'
import { CHRONICLE_ORACLE_PRECISION } from '@/config/chain/types'
import { ssrAuthOracleConfig } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { TokenDefinition } from '@/domain/types/TokenDefinition'
import { fromRay } from '@/utils/math'

export interface FetchPriceParams {
  token: TokenDefinition
  priceConfig: PriceConfig
  chainId: number
  wagmiConfig: Config
}

export async function fetchPrice({
  token,
  priceConfig,
  chainId,
  wagmiConfig,
}: FetchPriceParams): Promise<NormalizedNumber> {
  return fetchPriceByStrategy({ token, priceStrategy: priceConfig, chainId, wagmiConfig })
}

interface FetchPriceByStrategyParams {
  token: TokenDefinition
  priceStrategy: PriceStrategy
  chainId: number
  wagmiConfig: Config
}

async function fetchPriceByStrategy({
  token,
  priceStrategy,
  chainId,
  wagmiConfig,
}: FetchPriceByStrategyParams): Promise<NormalizedNumber> {
  switch (priceStrategy.type) {
    case 'fixed-usd':
      return NormalizedNumber(1)

    case 'zero-price':
      return NormalizedNumber.ZERO

    case 'ssr-auth-oracle':
      return fetchSsrAuthOraclePrice({ chainId, wagmiConfig })

    case 'vault':
      return fetchVaultPrice({
        token,
        sharesDecimals: priceStrategy.sharesDecimals,
        assetsDecimals: priceStrategy.assetsDecimals,
        chainId,
        wagmiConfig,
      })

    case 'chronicle':
      return fetchChroniclePrice({
        oracleAddress: priceStrategy.oracleAddress,
        chainId,
        wagmiConfig,
      })

    default:
      assertNever(priceStrategy)
  }
}

async function fetchSsrAuthOraclePrice({
  chainId,
  wagmiConfig,
}: {
  chainId: number
  wagmiConfig: Config
}): Promise<NormalizedNumber> {
  const result = await readContract(wagmiConfig, {
    abi: ssrAuthOracleConfig.abi,
    address: getContractAddress(ssrAuthOracleConfig.address, chainId),
    functionName: 'getConversionRate',
    chainId,
  })

  return NormalizedNumber(fromRay(bigNumberify(result)))
}

async function fetchVaultPrice({
  token,
  sharesDecimals,
  assetsDecimals,
  chainId,
  wagmiConfig,
}: {
  token: TokenDefinition
  sharesDecimals?: number
  assetsDecimals?: number
  chainId: number
  wagmiConfig: Config
}): Promise<NormalizedNumber> {
  const effectiveSharesDecimals = sharesDecimals ?? 18
  const effectiveAssetsDecimals = assetsDecimals ?? 18

  const result = await readContract(wagmiConfig, {
    abi: erc4626Abi,
    address: token.address,
    functionName: 'convertToAssets',
    args: [parseUnits('1', effectiveSharesDecimals)],
    chainId,
  })

  return NormalizedNumber(formatUnits(result, effectiveAssetsDecimals))
}

async function fetchChroniclePrice({
  oracleAddress,
  chainId,
  wagmiConfig,
}: {
  oracleAddress: `0x${string}`
  chainId: number
  wagmiConfig: Config
}): Promise<NormalizedNumber> {
  const result = await readContract(wagmiConfig, {
    account: zeroAddress,
    abi: chronicleOracleAbi,
    address: oracleAddress,
    functionName: 'latestAnswer',
    chainId,
  })

  return NormalizedNumber(formatUnits(result, CHRONICLE_ORACLE_PRECISION))
}
